import { LocationMediator } from 'shared/vidio_helpers';
import { localStorageAdapter } from "commons/BrowserStorage";
import { FetchAdapter } from '../FetchAdapter/FetchAdapter';

export const config = {
  entrypoint_uri: process.env.IAP_ENTRYPOINT_URI,
  project_id: process.env.IAP_PROJECT_ID,
  client_id: process.env.IAP_CLIENT_ID,
  client_secret: process.env.IAP_CLIENT_SECRET,
  audience_id: process.env.IAP_AUDIENCE_ID,
  auth_uri: "https://accounts.google.com/o/oauth2/auth",
  token_uri: "https://oauth2.googleapis.com/token",
  auth_provider_x509_cert_url: "https://www.googleapis.com/oauth2/v1/certs",
  storage_key: "iap_token",
  error_204_message: "Failed to get IAP Token, Google OAuth returns 204."
};

export class IAPTokenService {
  static init() {
    if (!this._isIAPEnabled() || this._isTokenAlive()) {
      return Promise.resolve();
    }

    const authCode = this._getQueryParam("code", LocationMediator.href());
    if (authCode) {
      return this._requestIdToken(authCode);
    } else {
      return this._goToGoogleSSO();
    }
  }

  static getToken() {
    // Because so far we're only using IAP on staging, we don't check if token has expired or not
    // for simplicity.
    // Token expiration will be checked each time a webpack entrypoint JS is run.
    return this._getIAPToken().token;
  }

  static _getQueryParam(key, url) {
    const pattern = /\?(.+)/;
    const match = url.match(pattern);
    if (!match) return "";
    return new URLSearchParams(match[0]).get(key) || "";
  }

  static _isIAPEnabled() {
    return this._isConfigValid() && this._isLocationValid();
  }

  static _isConfigValid() {
    return process.env.IAP_ENTRYPOINT_URI
      && process.env.IAP_PROJECT_ID
      && process.env.IAP_CLIENT_ID
      && process.env.IAP_CLIENT_SECRET
      && process.env.IAP_AUDIENCE_ID;
  }

  static _isLocationValid() {
    return LocationMediator.href().includes(config.entrypoint_uri);
  }

  static _isTokenAlive() {
    const iapToken = this._getIAPToken();
    return iapToken.expiresAt > Date.now();
  }

  static _getIAPToken() {
    const iapToken = localStorageAdapter().getItem(config.storage_key);
    return iapToken ? JSON.parse(iapToken) : {};
  }

  static _saveToken(token, expiresIn) {
    // since we're currently only using this in staging, we can just use localStorage for convenience
    // but if later is used in production, consider using Cookie instead since we can set expiry time
    localStorageAdapter().setItem(
      config.storage_key,
      JSON.stringify({
        "token": token,
        "expiresAt": Date.now() + parseInt(expiresIn) * 1000,
      })
    );
  }

  static _requestIdToken(code) {
    const payload = {
      client_id: config.client_id,
      client_secret: config.client_secret,
      redirect_uri: config.entrypoint_uri,
      audience: config.audience_id,
      grant_type: "authorization_code",
      code: code,
    };

    const postConfig = {
      body: JSON.stringify(payload),
      credentials: 'omit', // Google OAuth Token API does not allow cross-origin credentials, so we override the FetchAdapter's default "include"
      headers: {
        'Content-Type': 'application/json'
      }
    };

    return FetchAdapter.post(
      config.token_uri,
      postConfig
    )
    .then((response) => {
      if (response.status === 204) {
        throw Error(config.error_204_message);
      }

      const json = response.json()
      return json;
    })
    .then((data) => {
      this._saveToken(data.id_token, data.expires_in);
    })
    .then(() => {
      this._redirectTo(config.entrypoint_uri);
    });
  }

  static _goToGoogleSSO() {
    return Promise
      .resolve()
      .then(() => {
        this._redirectTo(`https://accounts.google.com/o/oauth2/v2/auth?client_id=${config.client_id}&response_type=code&scope=openid%20email&access_type=offline&redirect_uri=${config.entrypoint_uri}`);
      });
  }

  static _redirectTo(uri) {
    window.location.href = uri;
  }
}
